import React from 'react';
import { Button as MuiButton, ButtonProps as MuiButtonProps, styled } from '@mui/material';
import { designSystem } from '../../utils/designSystem';

/**
 * Standardized Button Component
 *
 * Enforces design system standards for buttons across the app.
 * Uses consistent sizing, spacing, and hierarchy.
 */

interface StyledButtonProps extends MuiButtonProps {
  hierarchy?: 'primary' | 'secondary' | 'tertiary';
}

const StyledButton = styled(MuiButton)<StyledButtonProps>(({ theme, hierarchy = 'primary', size = 'medium' }) => {
  // Size-specific styles
  const sizeStyles = {
    small: {
      height: designSystem.button.sizes.sm.height,
      padding: designSystem.button.sizes.sm.padding,
      fontSize: designSystem.button.sizes.sm.fontSize,
    },
    medium: {
      height: designSystem.button.sizes.md.height,
      padding: designSystem.button.sizes.md.padding,
      fontSize: designSystem.button.sizes.md.fontSize,
    },
    large: {
      height: designSystem.button.sizes.lg.height,
      padding: designSystem.button.sizes.lg.padding,
      fontSize: designSystem.button.sizes.lg.fontSize,
    },
  };

  // Hierarchy-specific styles
  const hierarchyStyles = {
    primary: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      boxShadow: designSystem.shadows.primarySm,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        boxShadow: designSystem.shadows.primaryMd,
        transform: 'translateY(-1px)',
      },
      '&:active': {
        transform: 'translateY(0)',
        boxShadow: designSystem.shadows.sm,
      },
    },
    secondary: {
      backgroundColor: 'transparent',
      color: theme.palette.primary.main,
      border: `2px solid ${theme.palette.primary.main}`,
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: designSystem.alpha(theme.palette.primary.main, 0.08),
        borderColor: theme.palette.primary.dark,
        transform: 'translateY(-1px)',
      },
      '&:active': {
        transform: 'translateY(0)',
      },
    },
    tertiary: {
      backgroundColor: 'transparent',
      color: theme.palette.text.secondary,
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: designSystem.alpha(theme.palette.primary.main, 0.04),
        color: theme.palette.primary.main,
      },
    },
  };

  return {
    ...(sizeStyles[size] || sizeStyles.medium),
    ...(hierarchyStyles[hierarchy]),
    borderRadius: `${designSystem.borderRadius.sm}px`,
    fontWeight: designSystem.typography.fontWeight.semibold,
    textTransform: 'none',
    transition: `all ${designSystem.transitions.duration.normal}ms ${designSystem.transitions.easing.easeInOut}`,
    '&.Mui-disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };
});

/**
 * Button Component
 *
 * @example
 * // Primary button (default)
 * <Button>Click Me</Button>
 *
 * // Secondary button (outlined)
 * <Button hierarchy="secondary">Cancel</Button>
 *
 * // Tertiary button (text only)
 * <Button hierarchy="tertiary">Learn More</Button>
 *
 * // With icon
 * <Button startIcon={<AddIcon />}>Add Hotel</Button>
 *
 * // Loading state
 * <Button loading>Saving...</Button>
 */
export const Button: React.FC<StyledButtonProps> = ({
  hierarchy = 'primary',
  children,
  ...props
}) => {
  return (
    <StyledButton hierarchy={hierarchy} {...props}>
      {children}
    </StyledButton>
  );
};

export default Button;
